import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '../../i18n/useTranslation';
import type { AnalyticsResult, ChartType } from '../../services/analyticsService';
import { useChartColors, useSeriesColors } from '../measurements/MeasurementTrendChart';
import { SERIES_COLOUR_SLOTS } from '../measurements/parameterColours';
import { formatCell } from './analyticsFormat';

/** Slices / categories drawn at most (the table shows everything). */
const MAX_PIE_SLICES = 12;
const MAX_POINTS = 1000;

export interface ResultChartProps {
  result: AnalyticsResult;
  type: Exclude<ChartType, 'table'>;
  x: string;
  y: readonly string[];
  /** Accessible name of the chart (the result's title). */
  label: string;
}

/**
 * The result drawn as a standard chart (bar, line, area, pie, scatter) with the
 * design system's data-series colours. Every series is named in the legend /
 * slice labels — colour is never the only cue — and the same data is in the
 * table under the chart.
 */
export function ResultChart({ result, type, x, y, label }: ResultChartProps) {
  const { t, i18n } = useTranslation();
  const axis = useChartColors(null);
  const palette = useSeriesColors(SERIES_COLOUR_SLOTS);
  const xIndex = result.columns.findIndex((c) => c.name === x);
  const yIndexes = y.map((name) => result.columns.findIndex((c) => c.name === name)).filter((i) => i >= 0);
  if (xIndex < 0 || yIndexes.length === 0) return null;

  const xColumn = result.columns[xIndex];
  const data = result.rows.slice(0, MAX_POINTS).map((row) => {
    const point: Record<string, unknown> = {
      __x: type === 'scatter' ? row[xIndex] : formatCell(row[xIndex], xColumn, i18n.language),
    };
    for (const i of yIndexes) point[result.columns[i].name] = typeof row[i] === 'number' ? row[i] : null;
    return point;
  });
  const color = (i: number): string => palette[i % palette.length] || axis.series;
  const tick = { fill: axis.axis, fontSize: 12 };
  const numberFormat = (v: unknown): string =>
    typeof v === 'number' ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(v) : String(v);
  const tooltip = (
    <Tooltip wrapperClassName="analytics-chart__tooltip" formatter={(value: unknown) => numberFormat(value)} />
  );
  const yNames = yIndexes.map((i) => result.columns[i].name);

  let chart;
  if (type === 'pie') {
    const name = yNames[0];
    const slices = data.slice(0, MAX_PIE_SLICES).map((d) => ({ name: String(d.__x), value: Number(d[name] ?? 0) }));
    chart = (
      <PieChart>
        <Pie data={slices} dataKey="value" nameKey="name" outerRadius="75%" label={({ name: n }) => String(n)} isAnimationActive={false}>
          {slices.map((s, i) => <Cell key={s.name} fill={color(i)} />)}
        </Pie>
        {tooltip}
      </PieChart>
    );
  } else if (type === 'scatter') {
    chart = (
      <ScatterChart>
        <CartesianGrid stroke={axis.grid} strokeDasharray="3 3" />
        <XAxis dataKey="__x" type="number" name={x} stroke={axis.axis} tick={tick} tickFormatter={numberFormat} />
        <YAxis dataKey={yNames[0]} type="number" name={yNames[0]} stroke={axis.axis} tick={tick} tickFormatter={numberFormat} />
        {tooltip}
        <Scatter data={data} fill={color(0)} name={yNames[0]} isAnimationActive={false} />
      </ScatterChart>
    );
  } else {
    const Chart = type === 'bar' ? BarChart : type === 'area' ? AreaChart : LineChart;
    chart = (
      <Chart data={data}>
        <CartesianGrid stroke={axis.grid} strokeDasharray="3 3" />
        <XAxis dataKey="__x" stroke={axis.axis} tick={tick} minTickGap={16} />
        <YAxis stroke={axis.axis} tick={tick} tickFormatter={numberFormat} width={56} />
        {tooltip}
        <Legend />
        {yNames.map((name, i) =>
          type === 'bar' ? (
            <Bar key={name} dataKey={name} fill={color(i)} isAnimationActive={false} />
          ) : type === 'area' ? (
            <Area key={name} dataKey={name} stroke={color(i)} fill={color(i)} fillOpacity={0.2} isAnimationActive={false} connectNulls />
          ) : (
            <Line key={name} dataKey={name} stroke={color(i)} strokeWidth={2} dot={data.length <= 60} isAnimationActive={false} connectNulls />
          ),
        )}
      </Chart>
    );
  }

  return (
    <figure className="analytics-chart" aria-label={label}>
      <div className="analytics-chart__canvas">
        <ResponsiveContainer width="100%" height="100%">
          {chart}
        </ResponsiveContainer>
      </div>
      <figcaption className="analytics-chart__caption">{t('analytics.chart.caption')}</figcaption>
    </figure>
  );
}

export default ResultChart;
